import React, { useEffect, useState } from 'react';
import { X, Swords, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials, formatNumber } from '../../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [
  { value: 60, label: '1 min' },
  { value: 120, label: '2 min' },
  { value: 180, label: '3 min' },
  { value: 300, label: '5 min' },
];

/**
 * BattleInviteModal — host can pick another active live stream and challenge it.
 */
export default function BattleInviteModal({ open, onClose, myStreamId, myUserId, token, onInvited }) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(180);
  const [inviting, setInviting] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/live/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const others = (r.data || []).filter((s) => s.id !== myStreamId && s.user_id !== myUserId);
        setStreams(others);
      })
      .catch(() => toast.error('Could not load active lives'))
      .finally(() => setLoading(false));
  }, [open, token, myStreamId, myUserId]);

  if (!open) return null;

  const handleInvite = async (opponent) => {
    setInviting(opponent.id);
    try {
      const r = await axios.post(
        `${API}/live/battles/invite`,
        {
          my_stream_id: myStreamId,
          opponent_stream_id: opponent.id,
          duration,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onInvited?.(r.data);
      toast.success(`Battle invitation sent to @${opponent.username}`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send invitation');
    }
    setInviting(null);
  };

  return (
    <div
      data-testid="battle-invite-modal"
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-neutral-900 text-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl
                   shadow-2xl ring-1 ring-white/10 max-h-[80vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Swords className="w-5 h-5 text-pink-400" />
            <h3 className="font-bold text-lg">Start a Battle</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10" data-testid="close-battle-invite">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 pb-2">
          <p className="text-xs text-white/60 mb-2">Battle duration</p>
          <div className="flex gap-2 overflow-x-auto">
            {DURATIONS.map((d) => (
              <button
                key={d.value}
                onClick={() => setDuration(d.value)}
                data-testid={`duration-${d.value}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition
                  ${duration === d.value
                    ? 'bg-pink-500 text-white'
                    : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-white/50" />
            </div>
          ) : streams.length === 0 ? (
            <p className="text-center py-8 text-white/50 text-sm">
              No other live streams to challenge right now.
            </p>
          ) : (
            streams.map((s) => (
              <div
                key={s.id}
                data-testid={`opponent-${s.id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition"
              >
                <Avatar className="w-10 h-10">
                  <AvatarImage src={s.avatar} />
                  <AvatarFallback className="bg-pink-700 text-white text-sm">
                    {getInitials(s.display_name || s.username)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">@{s.username}</p>
                  <p className="text-xs text-white/50 truncate">
                    {formatNumber(s.viewer_count || 0)} watching · {s.title}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                  onClick={() => handleInvite(s)}
                  disabled={inviting === s.id}
                  data-testid={`invite-${s.id}`}
                >
                  {inviting === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Challenge'}
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
